import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';
import {
  getScrollTop,
  getScrollEventTarget,
  getIntersectionRoot,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /** @type {EventTarget | null} */
  #scrollContainer = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
      root: getIntersectionRoot(),
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
      // The drawer squeeze can trigger minimum-reached at desktop widths where
      // it normally wouldn't. Once the menu hides, the overflow-list is
      // display:none and can't measure to clear it. Resetting it here so
      // setHeaderMenuStyle() sees a clean state.
      const overflowList = this.querySelector('overflow-list');
      if (overflowList) overflowList.removeAttribute('minimum-reached');
    }
    setHeaderMenuStyle();
  }

  /**
   * Rebinds the scroll listener and IntersectionObserver when the viewport
   * crosses the squeeze breakpoint (990px). The scroll container switches
   * between `.page-wrapper` (desktop) and `document.scrollingElement` (mobile),
   * so cached bindings from initialization become stale after a resize.
   */
  #handleBreakpointChange = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!stickyMode) return;

    // Rebind scroll listener
    if (this.#scrollContainer) {
      this.#scrollContainer.removeEventListener('scroll', this.#handleWindowScroll);
      this.#scrollContainer = getScrollEventTarget();
      this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
    }

    // Recreate IntersectionObserver with the new root
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#observeStickyPosition(stickyMode === 'always');
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = getScrollTop();
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';

      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        this.#scrollContainer = getScrollEventTarget();
        this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
      }

      scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    this.#scrollContainer?.removeEventListener('scroll', this.#handleWindowScroll);
    this.#scrollContainer = null;
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

// Anchor the shopify-account dialog to the bottom of the account button itself.
document.addEventListener(
  'open',
  (event) => {
    const { target } = event;
    if (!(target instanceof HTMLElement) || !target.matches('shopify-account')) return;

    const bottom = Math.max(0, Math.round(target.getBoundingClientRect().bottom));
    target.style.setProperty('--account-dialog-top', `${bottom}px`);
  },
  true
);

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});


document.addEventListener("DOMContentLoaded", function () {
          // العثور على العنصر الذي تريد الإضافة بعده
          let target = document.querySelector("header#shopify-section-sections--32923867447687__header_section");
  
          // كود HTML الذي تريد إضافته
          let newHTML = `
  <div id="freeShipMarqueeHost">
    <div class="free-ship-marquee" role="region" aria-label="Site highlights">
      <div class="free-ship-marquee__inner">
  
        <div class="free-ship-marquee__group">
  
          <div class="free-ship-marquee__item">
            <span>
              <svg style="height:14px !important;" viewBox="0 0 364 149" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M62.9439 68.2917C57.6768 68.2917 51.4995 72.5679 51.4995 77.8429V79.7532H88.6936L90.601 68.2917H62.9439Z" fill="#fff"></path>
                <path d="M21.935 93.125C21.935 87.85 28.1122 83.5737 33.3793 83.5737H87.7399L85.8325 95.0353H21.935V93.125Z" fill="#fff"></path>
                <path d="M41.0089 98.8558C35.7418 98.8558 29.5645 103.132 29.5645 108.407V110.317H83.9251L85.8325 98.8558H41.0089Z" fill="#fff"></path>
                <path d="M11.4443 114.138C6.17723 114.138 0 118.414 0 123.689V125.599H81.0641L82.9715 114.138H11.4443Z" fill="#fff"></path>
                <path fill-rule="evenodd" clip-rule="evenodd" d="M155.452 149C168.62 149 179.295 138.309 179.295 125.122C179.295 111.934 168.62 101.244 155.452 101.244C142.284 101.244 131.61 111.934 131.61 125.122C131.61 138.309 142.284 149 155.452 149ZM155.452 120.346C158.086 120.346 160.221 122.484 160.221 125.122C160.221 127.759 158.086 129.897 155.452 129.897C152.819 129.897 150.684 127.759 150.684 125.122C150.684 122.484 152.819 120.346 155.452 120.346Z" fill="#fff"></path>
                <path fill-rule="evenodd" clip-rule="evenodd" d="M319.488 149C332.656 149 343.33 138.309 343.33 125.122C343.33 111.934 332.656 101.244 319.488 101.244C306.32 101.244 295.645 111.934 295.645 125.122C295.645 138.309 306.32 149 319.488 149ZM319.488 120.346C322.121 120.346 324.256 122.484 324.256 125.122C324.256 127.759 322.121 129.897 319.488 129.897C316.854 129.897 314.719 127.759 314.719 125.122C314.719 122.484 316.854 120.346 319.488 120.346Z" fill="#fff"></path>
                <path fill-rule="evenodd" clip-rule="evenodd" d="M348.095 124.644H357.399C358.354 124.644 359.161 123.937 359.289 122.989L363.829 89.2672C364.447 84.6737 363.377 80.0118 360.818 76.1501L330.901 31.0126C329.133 28.3463 326.15 26.7436 322.955 26.7436H302.114C298.382 26.7436 295.198 29.4473 294.587 33.1344L279.801 122.421C279.608 123.585 280.504 124.644 281.682 124.644H290.881C291.135 109.039 303.846 96.4679 319.488 96.4679C335.13 96.4679 347.84 109.039 348.095 124.644ZM338.891 75.9326H301.032C298.699 75.9326 296.912 73.8532 297.26 71.5424L302.027 39.8843C302.309 38.0154 303.912 36.6335 305.799 36.6335H318.391C320.379 36.6335 322.225 37.6668 323.266 39.3629L342.141 70.1112C343.704 72.6568 341.875 75.9326 338.891 75.9326Z" fill="#fff"></path>
                <path d="M166.201 55.5468H174.87C177.025 55.5468 178.759 55.231 180.072 54.5996C181.399 53.9681 182.358 53.0735 182.949 51.9158C183.553 50.745 183.855 49.3505 183.855 47.7323C183.855 46.1142 183.553 44.7 182.949 43.4897C182.345 42.2793 181.379 41.3453 180.053 40.6875C178.726 40.0166 176.972 39.6811 174.792 39.6811H166.201V55.5468Z" fill="#fff"></path>
                <path fill-rule="evenodd" clip-rule="evenodd" d="M110.897 8.21067L95.0325 120.287C94.7069 122.588 96.4897 124.644 98.8095 124.644H126.845C127.1 109.039 139.81 96.4679 155.452 96.4679C171.094 96.4679 183.805 109.039 184.059 124.644H270.636C271.581 124.644 272.383 123.952 272.523 123.016L287.606 21.9256C289.328 10.3794 280.398 0 268.741 0H120.339C115.59 0 111.564 3.50067 110.897 8.21067ZM128.749 75.7538V35.3397H152.945V39.6811H133.635V53.3366H151.132V57.678H133.635V75.7538H128.749ZM161.314 35.3397V75.7538H166.201V59.9671H175.028C175.378 59.9671 175.72 59.9606 176.056 59.9476L184.565 75.7538H190.239L181.124 59.089C181.695 58.8944 182.231 58.6674 182.732 58.4081C184.742 57.3557 186.233 55.9086 187.205 54.0668C188.177 52.225 188.663 50.1135 188.663 47.7323C188.663 45.3512 188.177 43.2265 187.205 41.3584C186.233 39.4903 184.736 38.0235 182.713 36.9579C180.69 35.8791 178.102 35.3397 174.949 35.3397H161.314ZM221.475 75.7538H196.806V35.3397H221.16V39.6811H201.692V53.3366H219.899V57.678H201.692V71.4125H221.475V75.7538ZM254.637 75.7538H229.967V35.3397H254.321V39.6811H234.854V53.3366H253.06V57.678H234.854V71.4125H254.637V75.7538Z" fill="#fff"></path>
              </svg>
            </span>
            <span>Worldwide Shipping</span>
          </div>
  
          <div class="free-ship-marquee__item">
            <span><svg style="height:14px !important;" viewBox="0 0 206 156" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M0 16.1379C0 7.22538 7.21792 0 16.1217 0H175.548C184.452 0 191.67 7.22538 191.67 16.1379V26.8966H0V16.1379Z" fill="#fff"></path>
      <path d="M0 37.6552V45.7241H191.67V37.6552H0Z" fill="#fff"></path>
      <path fill-rule="evenodd" clip-rule="evenodd" d="M0 60.069H191.67V75.394C183.984 70.2445 174.742 67.2414 164.8 67.2414C138.089 67.2414 116.435 88.9171 116.435 115.655C116.435 123.743 118.416 131.368 121.919 138.069H16.1217C7.21792 138.069 0 130.844 0 121.931V60.069ZM28.6609 80.6897C26.6823 80.6897 25.0783 82.2954 25.0783 84.2759V104C25.0783 105.98 26.6823 107.586 28.6609 107.586H57.3217C59.3003 107.586 60.9043 105.98 60.9043 104V84.2759C60.9043 82.2954 59.3003 80.6897 57.3217 80.6897H28.6609Z" fill="#fff"></path>
      <path fill-rule="evenodd" clip-rule="evenodd" d="M206 114.759C206 137.536 187.554 156 164.8 156C142.046 156 123.6 137.536 123.6 114.759C123.6 91.9815 142.046 73.5172 164.8 73.5172C187.554 73.5172 206 91.9815 206 114.759ZM165.632 130.226C165.442 130.489 165.229 130.742 164.992 130.979C162.64 133.333 158.826 133.333 156.474 130.979L144.173 118.665C141.821 116.311 141.821 112.494 144.173 110.139C146.525 107.784 150.339 107.784 152.691 110.139L160.852 118.308L181.443 97.697C183.795 95.3427 187.608 95.3427 189.961 97.697C192.313 100.051 192.313 103.869 189.961 106.223L167.021 129.186C166.599 129.608 166.131 129.954 165.632 130.226Z" fill="#fff"></path>
    </svg></span>
            <span>Diversified Payment Options</span>
          </div>
  
          <div class="free-ship-marquee__item">
            <span><svg style="height:14px !important;" viewBox="0 0 135 156" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fill-rule="evenodd" clip-rule="evenodd" d="M3.05937 28.8241L64.0677 0.796107C66.385 -0.268497 69.0503 -0.265218 71.365 0.80509L131.96 28.8241C133.813 29.6808 135 31.5402 135 33.5866V64.3688C135 81.8625 130.107 99.0053 120.877 113.849C113.493 125.723 103.547 135.783 91.7702 143.288L75.4207 153.707C70.7055 156.712 64.6971 156.767 59.9278 153.85L43.2507 143.647C30.6297 135.925 20.0934 125.212 12.5678 112.45C4.34042 98.4972 0 82.5846 0 66.3746V33.6045C0 31.5481 1.19527 29.6805 3.05937 28.8241ZM54.7719 98.0808C57.6161 100.809 62.0365 100.862 64.9409 98.3084C65.3592 98.0247 65.7552 97.6944 66.1215 97.3174L106.081 56.2074C108.986 53.2189 108.925 48.4344 105.945 45.5208C102.965 42.6072 98.1949 42.6684 95.2899 45.6569L59.8901 82.0762L45.947 68.7015C42.9396 65.8165 38.1699 65.923 35.2935 68.9394C32.4172 71.9557 32.5234 76.7393 35.5309 79.6242L54.7719 98.0808Z" fill="#fff"></path>
    </svg></span>
            <span>30-Day Returns</span>
          </div>
  
          <div class="free-ship-marquee__item">
            <span><svg fill="#ffffff" style="height:14px !important;" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" stroke="#ffffff">
      <path d="M368.08,90.944l-67.065-9.744l-29.992-60.771c-6.116-12.393-23.836-12.386-29.949,0l-29.992,60.771l-67.065,9.744 c-13.676,1.987-19.145,18.842-9.254,28.483l48.529,47.304l-11.457,66.794c-2.337,13.618,12.003,24.034,24.229,17.604 l59.984-31.535l59.984,31.535c12.127,6.377,26.583-3.879,24.229-17.604l-11.456-66.794l48.529-47.304 C387.231,109.78,381.748,92.931,368.08,90.944z"></path>
      <path d="M120.435,253.713H16.699C7.477,253.713,0,261.189,0,270.411v213.754c0,9.222,7.477,16.699,16.699,16.699h103.736 c9.222,0,16.699-7.476,16.699-16.699V270.411C137.134,261.189,129.657,253.713,120.435,253.713z"></path>
      <path d="M501.108,326.303c-18.077-22.784-51.249-26.096-73.456-7.46l-79.113,66.383c-24.799,0-44.492,0-67.177,0l35.938-30.155 c27.34-22.941,12.375-68.954-24.879-68.954H170.532v198.715c8.896,7.193,20.04,11.154,31.496,11.154h156.498 c11.757,0,23.192-4.161,32.201-11.719l102.571-86.061C514.766,380.19,518.272,347.935,501.108,326.303z"></path>
    </svg></span>
            <span>200+ Happy Customer</span>
          </div>
  
        </div>
        <div class="free-ship-marquee__group">
  
          <div class="free-ship-marquee__item">
            <span>
              <svg style="height:14px !important;" viewBox="0 0 364 149" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M62.9439 68.2917C57.6768 68.2917 51.4995 72.5679 51.4995 77.8429V79.7532H88.6936L90.601 68.2917H62.9439Z" fill="#fff"></path>
                <path d="M21.935 93.125C21.935 87.85 28.1122 83.5737 33.3793 83.5737H87.7399L85.8325 95.0353H21.935V93.125Z" fill="#fff"></path>
                <path d="M41.0089 98.8558C35.7418 98.8558 29.5645 103.132 29.5645 108.407V110.317H83.9251L85.8325 98.8558H41.0089Z" fill="#fff"></path>
                <path d="M11.4443 114.138C6.17723 114.138 0 118.414 0 123.689V125.599H81.0641L82.9715 114.138H11.4443Z" fill="#fff"></path>
                <path fill-rule="evenodd" clip-rule="evenodd" d="M155.452 149C168.62 149 179.295 138.309 179.295 125.122C179.295 111.934 168.62 101.244 155.452 101.244C142.284 101.244 131.61 111.934 131.61 125.122C131.61 138.309 142.284 149 155.452 149ZM155.452 120.346C158.086 120.346 160.221 122.484 160.221 125.122C160.221 127.759 158.086 129.897 155.452 129.897C152.819 129.897 150.684 127.759 150.684 125.122C150.684 122.484 152.819 120.346 155.452 120.346Z" fill="#fff"></path>
                <path fill-rule="evenodd" clip-rule="evenodd" d="M319.488 149C332.656 149 343.33 138.309 343.33 125.122C343.33 111.934 332.656 101.244 319.488 101.244C306.32 101.244 295.645 111.934 295.645 125.122C295.645 138.309 306.32 149 319.488 149ZM319.488 120.346C322.121 120.346 324.256 122.484 324.256 125.122C324.256 127.759 322.121 129.897 319.488 129.897C316.854 129.897 314.719 127.759 314.719 125.122C314.719 122.484 316.854 120.346 319.488 120.346Z" fill="#fff"></path>
                <path fill-rule="evenodd" clip-rule="evenodd" d="M348.095 124.644H357.399C358.354 124.644 359.161 123.937 359.289 122.989L363.829 89.2672C364.447 84.6737 363.377 80.0118 360.818 76.1501L330.901 31.0126C329.133 28.3463 326.15 26.7436 322.955 26.7436H302.114C298.382 26.7436 295.198 29.4473 294.587 33.1344L279.801 122.421C279.608 123.585 280.504 124.644 281.682 124.644H290.881C291.135 109.039 303.846 96.4679 319.488 96.4679C335.13 96.4679 347.84 109.039 348.095 124.644ZM338.891 75.9326H301.032C298.699 75.9326 296.912 73.8532 297.26 71.5424L302.027 39.8843C302.309 38.0154 303.912 36.6335 305.799 36.6335H318.391C320.379 36.6335 322.225 37.6668 323.266 39.3629L342.141 70.1112C343.704 72.6568 341.875 75.9326 338.891 75.9326Z" fill="#fff"></path>
                <path d="M166.201 55.5468H174.87C177.025 55.5468 178.759 55.231 180.072 54.5996C181.399 53.9681 182.358 53.0735 182.949 51.9158C183.553 50.745 183.855 49.3505 183.855 47.7323C183.855 46.1142 183.553 44.7 182.949 43.4897C182.345 42.2793 181.379 41.3453 180.053 40.6875C178.726 40.0166 176.972 39.6811 174.792 39.6811H166.201V55.5468Z" fill="#fff"></path>
                <path fill-rule="evenodd" clip-rule="evenodd" d="M110.897 8.21067L95.0325 120.287C94.7069 122.588 96.4897 124.644 98.8095 124.644H126.845C127.1 109.039 139.81 96.4679 155.452 96.4679C171.094 96.4679 183.805 109.039 184.059 124.644H270.636C271.581 124.644 272.383 123.952 272.523 123.016L287.606 21.9256C289.328 10.3794 280.398 0 268.741 0H120.339C115.59 0 111.564 3.50067 110.897 8.21067ZM128.749 75.7538V35.3397H152.945V39.6811H133.635V53.3366H151.132V57.678H133.635V75.7538H128.749ZM161.314 35.3397V75.7538H166.201V59.9671H175.028C175.378 59.9671 175.72 59.9606 176.056 59.9476L184.565 75.7538H190.239L181.124 59.089C181.695 58.8944 182.231 58.6674 182.732 58.4081C184.742 57.3557 186.233 55.9086 187.205 54.0668C188.177 52.225 188.663 50.1135 188.663 47.7323C188.663 45.3512 188.177 43.2265 187.205 41.3584C186.233 39.4903 184.736 38.0235 182.713 36.9579C180.69 35.8791 178.102 35.3397 174.949 35.3397H161.314ZM221.475 75.7538H196.806V35.3397H221.16V39.6811H201.692V53.3366H219.899V57.678H201.692V71.4125H221.475V75.7538ZM254.637 75.7538H229.967V35.3397H254.321V39.6811H234.854V53.3366H253.06V57.678H234.854V71.4125H254.637V75.7538Z" fill="#fff"></path>
              </svg>
            </span>
            <span>Worldwide Shipping</span>
          </div>
  
          <div class="free-ship-marquee__item">
            <span><svg style="height:14px !important;" viewBox="0 0 206 156" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M0 16.1379C0 7.22538 7.21792 0 16.1217 0H175.548C184.452 0 191.67 7.22538 191.67 16.1379V26.8966H0V16.1379Z" fill="#fff"></path>
      <path d="M0 37.6552V45.7241H191.67V37.6552H0Z" fill="#fff"></path>
      <path fill-rule="evenodd" clip-rule="evenodd" d="M0 60.069H191.67V75.394C183.984 70.2445 174.742 67.2414 164.8 67.2414C138.089 67.2414 116.435 88.9171 116.435 115.655C116.435 123.743 118.416 131.368 121.919 138.069H16.1217C7.21792 138.069 0 130.844 0 121.931V60.069ZM28.6609 80.6897C26.6823 80.6897 25.0783 82.2954 25.0783 84.2759V104C25.0783 105.98 26.6823 107.586 28.6609 107.586H57.3217C59.3003 107.586 60.9043 105.98 60.9043 104V84.2759C60.9043 82.2954 59.3003 80.6897 57.3217 80.6897H28.6609Z" fill="#fff"></path>
      <path fill-rule="evenodd" clip-rule="evenodd" d="M206 114.759C206 137.536 187.554 156 164.8 156C142.046 156 123.6 137.536 123.6 114.759C123.6 91.9815 142.046 73.5172 164.8 73.5172C187.554 73.5172 206 91.9815 206 114.759ZM165.632 130.226C165.442 130.489 165.229 130.742 164.992 130.979C162.64 133.333 158.826 133.333 156.474 130.979L144.173 118.665C141.821 116.311 141.821 112.494 144.173 110.139C146.525 107.784 150.339 107.784 152.691 110.139L160.852 118.308L181.443 97.697C183.795 95.3427 187.608 95.3427 189.961 97.697C192.313 100.051 192.313 103.869 189.961 106.223L167.021 129.186C166.599 129.608 166.131 129.954 165.632 130.226Z" fill="#fff"></path>
    </svg></span>
            <span>Diversified Payment Options</span>
          </div>
  
          <div class="free-ship-marquee__item">
            <span><svg style="height:14px !important;" viewBox="0 0 135 156" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fill-rule="evenodd" clip-rule="evenodd" d="M3.05937 28.8241L64.0677 0.796107C66.385 -0.268497 69.0503 -0.265218 71.365 0.80509L131.96 28.8241C133.813 29.6808 135 31.5402 135 33.5866V64.3688C135 81.8625 130.107 99.0053 120.877 113.849C113.493 125.723 103.547 135.783 91.7702 143.288L75.4207 153.707C70.7055 156.712 64.6971 156.767 59.9278 153.85L43.2507 143.647C30.6297 135.925 20.0934 125.212 12.5678 112.45C4.34042 98.4972 0 82.5846 0 66.3746V33.6045C0 31.5481 1.19527 29.6805 3.05937 28.8241ZM54.7719 98.0808C57.6161 100.809 62.0365 100.862 64.9409 98.3084C65.3592 98.0247 65.7552 97.6944 66.1215 97.3174L106.081 56.2074C108.986 53.2189 108.925 48.4344 105.945 45.5208C102.965 42.6072 98.1949 42.6684 95.2899 45.6569L59.8901 82.0762L45.947 68.7015C42.9396 65.8165 38.1699 65.923 35.2935 68.9394C32.4172 71.9557 32.5234 76.7393 35.5309 79.6242L54.7719 98.0808Z" fill="#fff"></path>
    </svg></span>
            <span>30-Day Returns</span>
          </div>
  
          <div class="free-ship-marquee__item">
            <span><svg fill="#ffffff" style="height:14px !important;" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" stroke="#ffffff">
      <path d="M368.08,90.944l-67.065-9.744l-29.992-60.771c-6.116-12.393-23.836-12.386-29.949,0l-29.992,60.771l-67.065,9.744 c-13.676,1.987-19.145,18.842-9.254,28.483l48.529,47.304l-11.457,66.794c-2.337,13.618,12.003,24.034,24.229,17.604 l59.984-31.535l59.984,31.535c12.127,6.377,26.583-3.879,24.229-17.604l-11.456-66.794l48.529-47.304 C387.231,109.78,381.748,92.931,368.08,90.944z"></path>
      <path d="M120.435,253.713H16.699C7.477,253.713,0,261.189,0,270.411v213.754c0,9.222,7.477,16.699,16.699,16.699h103.736 c9.222,0,16.699-7.476,16.699-16.699V270.411C137.134,261.189,129.657,253.713,120.435,253.713z"></path>
      <path d="M501.108,326.303c-18.077-22.784-51.249-26.096-73.456-7.46l-79.113,66.383c-24.799,0-44.492,0-67.177,0l35.938-30.155 c27.34-22.941,12.375-68.954-24.879-68.954H170.532v198.715c8.896,7.193,20.04,11.154,31.496,11.154h156.498 c11.757,0,23.192-4.161,32.201-11.719l102.571-86.061C514.766,380.19,518.272,347.935,501.108,326.303z"></path>
    </svg></span>
            <span>200+ Happy Customer</span>
          </div>
  
        </div>
        <div class="free-ship-marquee__group">
  
          <div class="free-ship-marquee__item">
            <span>
              <svg style="height:14px !important;" viewBox="0 0 364 149" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M62.9439 68.2917C57.6768 68.2917 51.4995 72.5679 51.4995 77.8429V79.7532H88.6936L90.601 68.2917H62.9439Z" fill="#fff"></path>
                <path d="M21.935 93.125C21.935 87.85 28.1122 83.5737 33.3793 83.5737H87.7399L85.8325 95.0353H21.935V93.125Z" fill="#fff"></path>
                <path d="M41.0089 98.8558C35.7418 98.8558 29.5645 103.132 29.5645 108.407V110.317H83.9251L85.8325 98.8558H41.0089Z" fill="#fff"></path>
                <path d="M11.4443 114.138C6.17723 114.138 0 118.414 0 123.689V125.599H81.0641L82.9715 114.138H11.4443Z" fill="#fff"></path>
                <path fill-rule="evenodd" clip-rule="evenodd" d="M155.452 149C168.62 149 179.295 138.309 179.295 125.122C179.295 111.934 168.62 101.244 155.452 101.244C142.284 101.244 131.61 111.934 131.61 125.122C131.61 138.309 142.284 149 155.452 149ZM155.452 120.346C158.086 120.346 160.221 122.484 160.221 125.122C160.221 127.759 158.086 129.897 155.452 129.897C152.819 129.897 150.684 127.759 150.684 125.122C150.684 122.484 152.819 120.346 155.452 120.346Z" fill="#fff"></path>
                <path fill-rule="evenodd" clip-rule="evenodd" d="M319.488 149C332.656 149 343.33 138.309 343.33 125.122C343.33 111.934 332.656 101.244 319.488 101.244C306.32 101.244 295.645 111.934 295.645 125.122C295.645 138.309 306.32 149 319.488 149ZM319.488 120.346C322.121 120.346 324.256 122.484 324.256 125.122C324.256 127.759 322.121 129.897 319.488 129.897C316.854 129.897 314.719 127.759 314.719 125.122C314.719 122.484 316.854 120.346 319.488 120.346Z" fill="#fff"></path>
                <path fill-rule="evenodd" clip-rule="evenodd" d="M348.095 124.644H357.399C358.354 124.644 359.161 123.937 359.289 122.989L363.829 89.2672C364.447 84.6737 363.377 80.0118 360.818 76.1501L330.901 31.0126C329.133 28.3463 326.15 26.7436 322.955 26.7436H302.114C298.382 26.7436 295.198 29.4473 294.587 33.1344L279.801 122.421C279.608 123.585 280.504 124.644 281.682 124.644H290.881C291.135 109.039 303.846 96.4679 319.488 96.4679C335.13 96.4679 347.84 109.039 348.095 124.644ZM338.891 75.9326H301.032C298.699 75.9326 296.912 73.8532 297.26 71.5424L302.027 39.8843C302.309 38.0154 303.912 36.6335 305.799 36.6335H318.391C320.379 36.6335 322.225 37.6668 323.266 39.3629L342.141 70.1112C343.704 72.6568 341.875 75.9326 338.891 75.9326Z" fill="#fff"></path>
                <path d="M166.201 55.5468H174.87C177.025 55.5468 178.759 55.231 180.072 54.5996C181.399 53.9681 182.358 53.0735 182.949 51.9158C183.553 50.745 183.855 49.3505 183.855 47.7323C183.855 46.1142 183.553 44.7 182.949 43.4897C182.345 42.2793 181.379 41.3453 180.053 40.6875C178.726 40.0166 176.972 39.6811 174.792 39.6811H166.201V55.5468Z" fill="#fff"></path>
                <path fill-rule="evenodd" clip-rule="evenodd" d="M110.897 8.21067L95.0325 120.287C94.7069 122.588 96.4897 124.644 98.8095 124.644H126.845C127.1 109.039 139.81 96.4679 155.452 96.4679C171.094 96.4679 183.805 109.039 184.059 124.644H270.636C271.581 124.644 272.383 123.952 272.523 123.016L287.606 21.9256C289.328 10.3794 280.398 0 268.741 0H120.339C115.59 0 111.564 3.50067 110.897 8.21067ZM128.749 75.7538V35.3397H152.945V39.6811H133.635V53.3366H151.132V57.678H133.635V75.7538H128.749ZM161.314 35.3397V75.7538H166.201V59.9671H175.028C175.378 59.9671 175.72 59.9606 176.056 59.9476L184.565 75.7538H190.239L181.124 59.089C181.695 58.8944 182.231 58.6674 182.732 58.4081C184.742 57.3557 186.233 55.9086 187.205 54.0668C188.177 52.225 188.663 50.1135 188.663 47.7323C188.663 45.3512 188.177 43.2265 187.205 41.3584C186.233 39.4903 184.736 38.0235 182.713 36.9579C180.69 35.8791 178.102 35.3397 174.949 35.3397H161.314ZM221.475 75.7538H196.806V35.3397H221.16V39.6811H201.692V53.3366H219.899V57.678H201.692V71.4125H221.475V75.7538ZM254.637 75.7538H229.967V35.3397H254.321V39.6811H234.854V53.3366H253.06V57.678H234.854V71.4125H254.637V75.7538Z" fill="#fff"></path>
              </svg>
            </span>
            <span>Worldwide Shipping</span>
          </div>
  
          <div class="free-ship-marquee__item">
            <span><svg style="height:14px !important;" viewBox="0 0 206 156" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M0 16.1379C0 7.22538 7.21792 0 16.1217 0H175.548C184.452 0 191.67 7.22538 191.67 16.1379V26.8966H0V16.1379Z" fill="#fff"></path>
      <path d="M0 37.6552V45.7241H191.67V37.6552H0Z" fill="#fff"></path>
      <path fill-rule="evenodd" clip-rule="evenodd" d="M0 60.069H191.67V75.394C183.984 70.2445 174.742 67.2414 164.8 67.2414C138.089 67.2414 116.435 88.9171 116.435 115.655C116.435 123.743 118.416 131.368 121.919 138.069H16.1217C7.21792 138.069 0 130.844 0 121.931V60.069ZM28.6609 80.6897C26.6823 80.6897 25.0783 82.2954 25.0783 84.2759V104C25.0783 105.98 26.6823 107.586 28.6609 107.586H57.3217C59.3003 107.586 60.9043 105.98 60.9043 104V84.2759C60.9043 82.2954 59.3003 80.6897 57.3217 80.6897H28.6609Z" fill="#fff"></path>
      <path fill-rule="evenodd" clip-rule="evenodd" d="M206 114.759C206 137.536 187.554 156 164.8 156C142.046 156 123.6 137.536 123.6 114.759C123.6 91.9815 142.046 73.5172 164.8 73.5172C187.554 73.5172 206 91.9815 206 114.759ZM165.632 130.226C165.442 130.489 165.229 130.742 164.992 130.979C162.64 133.333 158.826 133.333 156.474 130.979L144.173 118.665C141.821 116.311 141.821 112.494 144.173 110.139C146.525 107.784 150.339 107.784 152.691 110.139L160.852 118.308L181.443 97.697C183.795 95.3427 187.608 95.3427 189.961 97.697C192.313 100.051 192.313 103.869 189.961 106.223L167.021 129.186C166.599 129.608 166.131 129.954 165.632 130.226Z" fill="#fff"></path>
    </svg></span>
            <span>Diversified Payment Options</span>
          </div>
  
          <div class="free-ship-marquee__item">
            <span><svg style="height:14px !important;" viewBox="0 0 135 156" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fill-rule="evenodd" clip-rule="evenodd" d="M3.05937 28.8241L64.0677 0.796107C66.385 -0.268497 69.0503 -0.265218 71.365 0.80509L131.96 28.8241C133.813 29.6808 135 31.5402 135 33.5866V64.3688C135 81.8625 130.107 99.0053 120.877 113.849C113.493 125.723 103.547 135.783 91.7702 143.288L75.4207 153.707C70.7055 156.712 64.6971 156.767 59.9278 153.85L43.2507 143.647C30.6297 135.925 20.0934 125.212 12.5678 112.45C4.34042 98.4972 0 82.5846 0 66.3746V33.6045C0 31.5481 1.19527 29.6805 3.05937 28.8241ZM54.7719 98.0808C57.6161 100.809 62.0365 100.862 64.9409 98.3084C65.3592 98.0247 65.7552 97.6944 66.1215 97.3174L106.081 56.2074C108.986 53.2189 108.925 48.4344 105.945 45.5208C102.965 42.6072 98.1949 42.6684 95.2899 45.6569L59.8901 82.0762L45.947 68.7015C42.9396 65.8165 38.1699 65.923 35.2935 68.9394C32.4172 71.9557 32.5234 76.7393 35.5309 79.6242L54.7719 98.0808Z" fill="#fff"></path>
    </svg></span>
            <span>30-Day Returns</span>
          </div>
  
          <div class="free-ship-marquee__item">
            <span><svg fill="#ffffff" style="height:14px !important;" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" stroke="#ffffff">
      <path d="M368.08,90.944l-67.065-9.744l-29.992-60.771c-6.116-12.393-23.836-12.386-29.949,0l-29.992,60.771l-67.065,9.744 c-13.676,1.987-19.145,18.842-9.254,28.483l48.529,47.304l-11.457,66.794c-2.337,13.618,12.003,24.034,24.229,17.604 l59.984-31.535l59.984,31.535c12.127,6.377,26.583-3.879,24.229-17.604l-11.456-66.794l48.529-47.304 C387.231,109.78,381.748,92.931,368.08,90.944z"></path>
      <path d="M120.435,253.713H16.699C7.477,253.713,0,261.189,0,270.411v213.754c0,9.222,7.477,16.699,16.699,16.699h103.736 c9.222,0,16.699-7.476,16.699-16.699V270.411C137.134,261.189,129.657,253.713,120.435,253.713z"></path>
      <path d="M501.108,326.303c-18.077-22.784-51.249-26.096-73.456-7.46l-79.113,66.383c-24.799,0-44.492,0-67.177,0l35.938-30.155 c27.34-22.941,12.375-68.954-24.879-68.954H170.532v198.715c8.896,7.193,20.04,11.154,31.496,11.154h156.498 c11.757,0,23.192-4.161,32.201-11.719l102.571-86.061C514.766,380.19,518.272,347.935,501.108,326.303z"></path>
    </svg></span>
            <span>200+ Happy Customer</span>
          </div>
  
        </div>
        <div class="free-ship-marquee__group">
  
          <div class="free-ship-marquee__item">
            <span>
              <svg style="height:14px !important;" viewBox="0 0 364 149" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M62.9439 68.2917C57.6768 68.2917 51.4995 72.5679 51.4995 77.8429V79.7532H88.6936L90.601 68.2917H62.9439Z" fill="#fff"></path>
                <path d="M21.935 93.125C21.935 87.85 28.1122 83.5737 33.3793 83.5737H87.7399L85.8325 95.0353H21.935V93.125Z" fill="#fff"></path>
                <path d="M41.0089 98.8558C35.7418 98.8558 29.5645 103.132 29.5645 108.407V110.317H83.9251L85.8325 98.8558H41.0089Z" fill="#fff"></path>
                <path d="M11.4443 114.138C6.17723 114.138 0 118.414 0 123.689V125.599H81.0641L82.9715 114.138H11.4443Z" fill="#fff"></path>
                <path fill-rule="evenodd" clip-rule="evenodd" d="M155.452 149C168.62 149 179.295 138.309 179.295 125.122C179.295 111.934 168.62 101.244 155.452 101.244C142.284 101.244 131.61 111.934 131.61 125.122C131.61 138.309 142.284 149 155.452 149ZM155.452 120.346C158.086 120.346 160.221 122.484 160.221 125.122C160.221 127.759 158.086 129.897 155.452 129.897C152.819 129.897 150.684 127.759 150.684 125.122C150.684 122.484 152.819 120.346 155.452 120.346Z" fill="#fff"></path>
                <path fill-rule="evenodd" clip-rule="evenodd" d="M319.488 149C332.656 149 343.33 138.309 343.33 125.122C343.33 111.934 332.656 101.244 319.488 101.244C306.32 101.244 295.645 111.934 295.645 125.122C295.645 138.309 306.32 149 319.488 149ZM319.488 120.346C322.121 120.346 324.256 122.484 324.256 125.122C324.256 127.759 322.121 129.897 319.488 129.897C316.854 129.897 314.719 127.759 314.719 125.122C314.719 122.484 316.854 120.346 319.488 120.346Z" fill="#fff"></path>
                <path fill-rule="evenodd" clip-rule="evenodd" d="M348.095 124.644H357.399C358.354 124.644 359.161 123.937 359.289 122.989L363.829 89.2672C364.447 84.6737 363.377 80.0118 360.818 76.1501L330.901 31.0126C329.133 28.3463 326.15 26.7436 322.955 26.7436H302.114C298.382 26.7436 295.198 29.4473 294.587 33.1344L279.801 122.421C279.608 123.585 280.504 124.644 281.682 124.644H290.881C291.135 109.039 303.846 96.4679 319.488 96.4679C335.13 96.4679 347.84 109.039 348.095 124.644ZM338.891 75.9326H301.032C298.699 75.9326 296.912 73.8532 297.26 71.5424L302.027 39.8843C302.309 38.0154 303.912 36.6335 305.799 36.6335H318.391C320.379 36.6335 322.225 37.6668 323.266 39.3629L342.141 70.1112C343.704 72.6568 341.875 75.9326 338.891 75.9326Z" fill="#fff"></path>
                <path d="M166.201 55.5468H174.87C177.025 55.5468 178.759 55.231 180.072 54.5996C181.399 53.9681 182.358 53.0735 182.949 51.9158C183.553 50.745 183.855 49.3505 183.855 47.7323C183.855 46.1142 183.553 44.7 182.949 43.4897C182.345 42.2793 181.379 41.3453 180.053 40.6875C178.726 40.0166 176.972 39.6811 174.792 39.6811H166.201V55.5468Z" fill="#fff"></path>
                <path fill-rule="evenodd" clip-rule="evenodd" d="M110.897 8.21067L95.0325 120.287C94.7069 122.588 96.4897 124.644 98.8095 124.644H126.845C127.1 109.039 139.81 96.4679 155.452 96.4679C171.094 96.4679 183.805 109.039 184.059 124.644H270.636C271.581 124.644 272.383 123.952 272.523 123.016L287.606 21.9256C289.328 10.3794 280.398 0 268.741 0H120.339C115.59 0 111.564 3.50067 110.897 8.21067ZM128.749 75.7538V35.3397H152.945V39.6811H133.635V53.3366H151.132V57.678H133.635V75.7538H128.749ZM161.314 35.3397V75.7538H166.201V59.9671H175.028C175.378 59.9671 175.72 59.9606 176.056 59.9476L184.565 75.7538H190.239L181.124 59.089C181.695 58.8944 182.231 58.6674 182.732 58.4081C184.742 57.3557 186.233 55.9086 187.205 54.0668C188.177 52.225 188.663 50.1135 188.663 47.7323C188.663 45.3512 188.177 43.2265 187.205 41.3584C186.233 39.4903 184.736 38.0235 182.713 36.9579C180.69 35.8791 178.102 35.3397 174.949 35.3397H161.314ZM221.475 75.7538H196.806V35.3397H221.16V39.6811H201.692V53.3366H219.899V57.678H201.692V71.4125H221.475V75.7538ZM254.637 75.7538H229.967V35.3397H254.321V39.6811H234.854V53.3366H253.06V57.678H234.854V71.4125H254.637V75.7538Z" fill="#fff"></path>
              </svg>
            </span>
            <span>Worldwide Shipping</span>
          </div>
  
          <div class="free-ship-marquee__item">
            <span><svg style="height:14px !important;" viewBox="0 0 206 156" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M0 16.1379C0 7.22538 7.21792 0 16.1217 0H175.548C184.452 0 191.67 7.22538 191.67 16.1379V26.8966H0V16.1379Z" fill="#fff"></path>
      <path d="M0 37.6552V45.7241H191.67V37.6552H0Z" fill="#fff"></path>
      <path fill-rule="evenodd" clip-rule="evenodd" d="M0 60.069H191.67V75.394C183.984 70.2445 174.742 67.2414 164.8 67.2414C138.089 67.2414 116.435 88.9171 116.435 115.655C116.435 123.743 118.416 131.368 121.919 138.069H16.1217C7.21792 138.069 0 130.844 0 121.931V60.069ZM28.6609 80.6897C26.6823 80.6897 25.0783 82.2954 25.0783 84.2759V104C25.0783 105.98 26.6823 107.586 28.6609 107.586H57.3217C59.3003 107.586 60.9043 105.98 60.9043 104V84.2759C60.9043 82.2954 59.3003 80.6897 57.3217 80.6897H28.6609Z" fill="#fff"></path>
      <path fill-rule="evenodd" clip-rule="evenodd" d="M206 114.759C206 137.536 187.554 156 164.8 156C142.046 156 123.6 137.536 123.6 114.759C123.6 91.9815 142.046 73.5172 164.8 73.5172C187.554 73.5172 206 91.9815 206 114.759ZM165.632 130.226C165.442 130.489 165.229 130.742 164.992 130.979C162.64 133.333 158.826 133.333 156.474 130.979L144.173 118.665C141.821 116.311 141.821 112.494 144.173 110.139C146.525 107.784 150.339 107.784 152.691 110.139L160.852 118.308L181.443 97.697C183.795 95.3427 187.608 95.3427 189.961 97.697C192.313 100.051 192.313 103.869 189.961 106.223L167.021 129.186C166.599 129.608 166.131 129.954 165.632 130.226Z" fill="#fff"></path>
    </svg></span>
            <span>Diversified Payment Options</span>
          </div>
  
          <div class="free-ship-marquee__item">
            <span><svg style="height:14px !important;" viewBox="0 0 135 156" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fill-rule="evenodd" clip-rule="evenodd" d="M3.05937 28.8241L64.0677 0.796107C66.385 -0.268497 69.0503 -0.265218 71.365 0.80509L131.96 28.8241C133.813 29.6808 135 31.5402 135 33.5866V64.3688C135 81.8625 130.107 99.0053 120.877 113.849C113.493 125.723 103.547 135.783 91.7702 143.288L75.4207 153.707C70.7055 156.712 64.6971 156.767 59.9278 153.85L43.2507 143.647C30.6297 135.925 20.0934 125.212 12.5678 112.45C4.34042 98.4972 0 82.5846 0 66.3746V33.6045C0 31.5481 1.19527 29.6805 3.05937 28.8241ZM54.7719 98.0808C57.6161 100.809 62.0365 100.862 64.9409 98.3084C65.3592 98.0247 65.7552 97.6944 66.1215 97.3174L106.081 56.2074C108.986 53.2189 108.925 48.4344 105.945 45.5208C102.965 42.6072 98.1949 42.6684 95.2899 45.6569L59.8901 82.0762L45.947 68.7015C42.9396 65.8165 38.1699 65.923 35.2935 68.9394C32.4172 71.9557 32.5234 76.7393 35.5309 79.6242L54.7719 98.0808Z" fill="#fff"></path>
    </svg></span>
            <span>30-Day Returns</span>
          </div>
  
          <div class="free-ship-marquee__item">
            <span><svg fill="#ffffff" style="height:14px !important;" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" stroke="#ffffff">
      <path d="M368.08,90.944l-67.065-9.744l-29.992-60.771c-6.116-12.393-23.836-12.386-29.949,0l-29.992,60.771l-67.065,9.744 c-13.676,1.987-19.145,18.842-9.254,28.483l48.529,47.304l-11.457,66.794c-2.337,13.618,12.003,24.034,24.229,17.604 l59.984-31.535l59.984,31.535c12.127,6.377,26.583-3.879,24.229-17.604l-11.456-66.794l48.529-47.304 C387.231,109.78,381.748,92.931,368.08,90.944z"></path>
      <path d="M120.435,253.713H16.699C7.477,253.713,0,261.189,0,270.411v213.754c0,9.222,7.477,16.699,16.699,16.699h103.736 c9.222,0,16.699-7.476,16.699-16.699V270.411C137.134,261.189,129.657,253.713,120.435,253.713z"></path>
      <path d="M501.108,326.303c-18.077-22.784-51.249-26.096-73.456-7.46l-79.113,66.383c-24.799,0-44.492,0-67.177,0l35.938-30.155 c27.34-22.941,12.375-68.954-24.879-68.954H170.532v198.715c8.896,7.193,20.04,11.154,31.496,11.154h156.498 c11.757,0,23.192-4.161,32.201-11.719l102.571-86.061C514.766,380.19,518.272,347.935,501.108,326.303z"></path>
    </svg></span>
            <span>200+ Happy Customer</span>
          </div>
  
        </div>
  
  
  
      </div>
    </div>
  </div> 


  <style>
       #freeShipMarqueeHost {
          grid-row: 1 / 2 !important;
          grid-column: 1 / 2 !important;
      }
  
      .free-ship-marquee {
          width: 100%;
          background: #f85932;
          color: #fff;
          overflow: hidden;
          position: relative
      }

      .free-ship-marquee.2 {  
          background: #496c31 !important;
      }
  
      .free-ship-marquee__inner {
          display: flex;
          width: max-content;
          white-space: nowrap;
          animation: fsm-scroll 40s linear infinite
      }
  
      .free-ship-marquee__group {
          display: inline-flex;
          align-items: center
      }
  
      .free-ship-marquee__item {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 28px;
          font-size: 14px;
          font-weight: 700;
          letter-spacing: .2px
      }
  
      .free-ship-marquee__dot {
          display: inline-block;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #353535;
          flex: 0 0 6px
      }
  
      @keyframes fsm-scroll {
          0% {
              transform: translateX(0)
          }
  
          100% {
              transform: translateX(-50%)
          }
      }
  
  </style>
          `;
  
          // إدراج الكود قبل العنصر المستهدف
          target.insertAdjacentHTML("beforebegin", newHTML);
      });